import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { Modal } from '@/components/dialogs/modal'
import { TextInput } from '@/components/inputs/text-input'
import { Show } from '@/components/show'
import { SimpleTable } from '@/components/tables/simple-table'
import { Button } from '@/components/ui/button'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import { useChooseCourses } from './hooks/use-choose-courses'

interface ChooseCoursesProps {
  availableCollegeCourses?: DesiredEducationCourse[]
  disabled?: boolean
  showDisciplineButton?: boolean
  formMethods?: StudentRequestedCoursesFormMethods
  variant?: 'manager' | 'student'
}

export const ChooseCourses = ({
  availableCollegeCourses = [],
  disabled,
  showDisciplineButton = true,
  formMethods,
  variant = 'manager'
}: ChooseCoursesProps) => {
  const manualEquivalences =
    useWatch({
      control: formMethods?.control,
      name: 'courseEquivalences'
    }) ?? []

  const automaticEquivalences =
    useWatch({
      control: formMethods?.control,
      name: 'automaticCourseEquivalences'
    }) ?? []

  const equivalences = useMemo(() => {
    return [
      ...(manualEquivalences ?? []),
      ...(automaticEquivalences ?? [])
    ].filter(Boolean)
  }, [manualEquivalences, automaticEquivalences])

  const collegeCourses = availableCollegeCourses ?? []

  const availableCourses = useMemo(() => {
    return (
      collegeCourses?.filter(
        (available) =>
          !equivalences.some((selected) => {
            const desiredCourseId = selected?.desiredCourse?.courseId
            return (
              desiredCourseId != null && desiredCourseId === available.courseId
            )
          })
      ) ?? []
    )
  }, [collegeCourses, equivalences])

  const {
    states: { isOpen, searchText, tableConfig, localSelectedCollegeCourses },
    actions: {
      setOpen,
      handleSearchInput,
      setLocalSelectedCollegeCourses,
      handleSubmit
    }
  } = useChooseCourses(availableCourses, formMethods, variant)

  return (
    <>
      <Show when={showDisciplineButton}>
        <Button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(!isOpen)}
          variant={'light'}
          className="flex items-center gap-2 h-8"
        >
          <p className="text-[var(--brand-blue-3)] font-bold">
            Adicionar Disciplina +
          </p>
        </Button>
      </Show>

      <Modal
        isOpen={isOpen}
        setOpen={setOpen}
        title={'Adicionar Disciplinas'}
        className="sm:max-w-none w-250"
        backButtonLabel="Voltar"
        submitButtonLabel="Selecionar"
        submitAction={handleSubmit}
        description="Seleção de disciplinas"
      >
        <div className="mt-4">
          <TextInput
            onChange={handleSearchInput}
            className="bg-input/40 h-10 mb-2"
            placeholder="Pesquise"
            value={searchText}
          />
        </div>
        <div className="w-full h-100 overflow-y-auto">
          <SimpleTable
            tableConfig={tableConfig}
            selectable={true}
            selectedItems={localSelectedCollegeCourses}
            setSelectedItems={setLocalSelectedCollegeCourses}
            selectIcon="square"
          />
        </div>
      </Modal>
    </>
  )
}
